import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, TextArea, TextInput } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { ExportButton } from '../components/Filters';
import { CURRENCY, Money, When } from '../components/format';
import { MoneyInput } from '../components/MoneyInput';

type Spend = { id: string; source: string; month: string; amount: string; note: string; updatedAt: string };

const columns: ColumnDef<Spend, any>[] = [
  { header: 'Tháng', accessorKey: 'month', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Nguồn', accessorKey: 'source' },
  { header: `Chi phí (${CURRENCY.symbol})`, accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Ghi chú', accessorKey: 'note', enableSorting: false, cell: (c) => c.getValue() || <span className="muted">-</span> },
  { header: 'Sửa lần cuối', accessorKey: 'updatedAt', cell: (c) => <When v={c.getValue()} /> },
];

const thisMonth = () => new Date().toISOString().slice(0, 7);

function SpendDialog({ row, open, onClose }: { row: Spend | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const editing = !!row;
  const [source, setSource] = useState(row?.source ?? '');
  const [month, setMonth] = useState(row?.month ?? thisMonth());
  const [amount, setAmount] = useState(row?.amount ?? '');
  const [note, setNote] = useState(row?.note ?? '');
  const done = () => { onClose(); qc.invalidateQueries({ queryKey: ['marketing-spend'] }); };
  const save = useMutation({
    mutationFn: () => editing
      ? api.patch(`/api/admin/marketing-spend/${row!.id}`, { amount, note })
      : api.post('/api/admin/marketing-spend', { source: source.trim().toLowerCase(), month, amount, note }),
    onSuccess: done,
  });
  const del = useMutation({ mutationFn: () => api.del(`/api/admin/marketing-spend/${row!.id}`), onSuccess: done });
  const srcOk = /^[a-z0-9._-]{1,64}$/.test(source.trim().toLowerCase());
  const ready = (editing || (srcOk && /^\d{4}-(0[1-9]|1[0-2])$/.test(month))) && amount !== '' && !save.isPending;
  const canEdit = can('marketing.manage');
  return (
    <Modal open={open} modalHeading={editing ? `Chi phí ${row!.source}, tháng ${row!.month}` : 'Nhập chi phí marketing'}
      primaryButtonText="Lưu" secondaryButtonText="Hủy" primaryButtonDisabled={!ready || !canEdit}
      onRequestClose={onClose} onRequestSubmit={() => ready && save.mutate()}>
      <div className="stack">
        {editing ? null : <>
          <TextInput id="mk-src" labelText="Nguồn" helperText="Trùng tên utm_source trên link quảng cáo, ví dụ facebook, tiktok, google."
            invalid={source !== '' && !srcOk} invalidText="Chỉ chữ thường, số và . _ -" value={source} onChange={(e) => setSource(e.target.value)} />
          <TextInput id="mk-month" type="month" labelText="Tháng" value={month} onChange={(e) => setMonth(e.target.value)} />
        </>}
        <MoneyInput id="mk-amt" labelText={`Chi phí cả tháng (${CURRENCY.symbol})`} value={amount} onChange={setAmount} />
        <TextArea id="mk-note" labelText="Ghi chú (tùy chọn)" rows={2} maxCount={500} enableCounter value={note} onChange={(e) => setNote(e.target.value)} />
        {editing && canEdit ? (
          <div className="row"><Button kind="danger--ghost" size="sm" disabled={del.isPending} onClick={() => del.mutate()}>Xóa dòng này</Button></div>
        ) : null}
        <InlineError error={save.error ?? del.error} />
      </div>
    </Modal>
  );
}

export function MarketingPage() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['marketing-spend'], queryFn: () => api.get<{ items: Spend[] }>('/api/admin/marketing-spend') });
  const [dialog, setDialog] = useState<{ row: Spend | null } | null>(null);
  const total = (q.data?.items ?? []).filter((r) => r.month === thisMonth()).reduce((a, r) => a + BigInt(r.amount), 0n);
  return (
    <div className="page">
      <PageHeader title="Chi phí marketing"
        lead="Chi phí theo nguồn và tháng, để tính chi phí cho mỗi người nạp đầu và hiệu quả từng kênh."
        actions={<>
          <ExportButton path="/api/admin/marketing-spend" query={{}} />
          {can('marketing.manage') ? <Button size="sm" renderIcon={Add} onClick={() => setDialog({ row: null })}>Nhập chi phí</Button> : null}
        </>} />
      <div className="kpis">
        <div className="kpi"><span>Tháng này</span><strong><Money v={total} unit /></strong></div>
      </div>
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
        onRowClick={(r) => setDialog({ row: r })} rowKey={(r) => r.id}
        empty={{ title: 'Chưa có chi phí nào', body: 'Nhập chi phí từng kênh theo tháng để báo cáo tính được hiệu quả.' }} />
      {dialog ? <SpendDialog key={dialog.row?.id ?? 'new'} row={dialog.row} open onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
